import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useDebounce } from '@/lib/useDebounce';
import {
  searchUsers,
  getRecentUsers,
  deleteUser,
  assignPlanToUser,
  generatePasswordResetLink,
  UserProps,
} from '@/api/usersService';
import { queryKeys } from '@/lib/queryKeys';
import { toaster } from '@/components/ui/toaster';

interface AssignPlanParams {
  userId: string;
  planId: string;
  competitionDate: Date;
  latestPlan: string | null;
  weekCount: number;
}

export const useAdminUsers = (searchTerm: string | null) => {
  const queryClient = useQueryClient();
  const debouncedTerm = useDebounce(searchTerm, 500);
  const isSearching = !!debouncedTerm && debouncedTerm.length >= 3;

  const recentQuery = useQuery<UserProps[]>({
    queryKey: queryKeys.usersRecent,
    queryFn: () => getRecentUsers(15),
    enabled: !isSearching,
    staleTime: 30 * 1000,
  });

  const searchQuery = useQuery<UserProps[]>({
    queryKey: queryKeys.userSearch(debouncedTerm ?? ''),
    queryFn: () => searchUsers(debouncedTerm!),
    enabled: isSearching,
    staleTime: 30 * 1000,
  });

  const invalidateUsers = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.usersRecent });
    queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
  };

  const deleteUserMutation = useMutation({
    mutationFn: deleteUser,
    onSuccess: () => {
      invalidateUsers();
      toaster.create({ title: 'Utilisateur supprimé', type: 'success' });
    },
    onError: (error) => {
      toaster.create({
        title: 'Échec de la suppression',
        description: error instanceof Error ? error.message : 'Une erreur est survenue',
        type: 'error',
      });
    },
  });

  const assignPlanMutation = useMutation({
    mutationFn: ({ userId, planId, competitionDate, latestPlan, weekCount }: AssignPlanParams) =>
      assignPlanToUser(userId, planId, competitionDate, latestPlan, weekCount),
    onSuccess: () => {
      invalidateUsers();
      toaster.create({ title: 'Plan attribué', type: 'success' });
    },
    onError: (error) => {
      toaster.create({
        title: "Échec de l'attribution du plan",
        description: error instanceof Error ? error.message : 'Une erreur est survenue',
        type: 'error',
      });
    },
  });

  const resetLinkMutation = useMutation({
    mutationFn: generatePasswordResetLink,
    onError: (error) => {
      toaster.create({
        title: 'Échec de la génération du lien',
        description: error instanceof Error ? error.message : 'Une erreur est survenue',
        type: 'error',
      });
    },
  });

  return {
    users: isSearching ? searchQuery.data ?? [] : recentQuery.data ?? [],
    isLoading: isSearching ? searchQuery.isLoading : recentQuery.isLoading,
    isSearching,
    deleteUser: deleteUserMutation.mutate,
    assignPlan: assignPlanMutation.mutate,
    resetLink: resetLinkMutation,
  };
};
